import { useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { canRewardAds, noAdReason } from '@/game/ads/availability';
import { nativeBridge, requestNativeRewardedAd } from '@/game/ads/nativeBridge';
import { useSession } from '@/store/session';
import { ads } from '@/api/endpoints';
import { sound } from '@/game/audio/sound';
import { useRun } from '@/store/run';
import { Icon, type IconName } from '@/components/ui/Icon';
import { ItemIcon } from '@/components/ui/ItemIcon';

/**
 * The loot card — the one place the Shwe Kyat rule becomes visible.
 *
 *   trash    → coins now; an optional ad doubles them. Kyat never appears.
 *   treasure → coins now; Kyat shown LOCKED until a rewarded ad verifies.
 *              Skip it and the coins are still paid, the Kyat is forfeited.
 *
 * The client never grants the Kyat. It requests an impression, plays the ad, then
 * hands the impression id to /cave/claim so the server can check its own SSV
 * record before crediting anything. See docs/ARCHITECTURE.md §3.
 */
/**
 * `failed` is not `skipped`, and conflating them was a bug with money on the far side.
 *
 * `skipped` means the player closed the ad — a choice, and on a treasure rock it correctly
 * strikes the Kyat row through as forfeited. `failed` means the ad could not play at all: no
 * fill, no network, an SDK refusal. Reporting that as "forfeited" tells a player they gave up
 * a payout they never got the chance to earn, and it silently removed the only button that
 * could have paid it. A failure keeps the offer and says what went wrong.
 */
type AdState = 'idle' | 'playing' | 'verified' | 'skipped' | 'failed';

export function LootCard() {
  const loot = useRun((s) => s.loot);
  const claim = useRun((s) => s.claim);
  const collect = useRun((s) => s.collect);
  const dismiss = useRun((s) => s.dismissLoot);
  const flags = useSession((s) => s.flags);

  /* Only offer an ad that can actually pay. On web AdMob sends no SSV, so the
     server can never verify one and the player would watch it for nothing. */
  const adsCanReward = canRewardAds(flags);

  const [busy, setBusy] = useState(false);
  const [adState, setAdState] = useState<AdState>('idle');
  const [adError, setAdError] = useState<string | null>(null);
  const [impression, setImpression] = useState<string | null>(null);
  const [shownCoins, setShownCoins] = useState(0);

  /*
   * The reveal has a sound, and it was written months ago and never played.
   *
   * `sound.ts` carries a five-second treasure jingle, a coin pickup and a
   * level-up fanfare; none of them had a caller anywhere in the app. The rock
   * breaking open is the payoff of the entire loop and it happened in silence.
   *
   * Treasure gets the jingle, trash gets the coin — the same distinction the card
   * makes visually, so the audio does not promise Kyat on a rock that can never
   * pay it (money rule 1).
   */
  useEffect(() => {
    if (!loot) return;
    /* `pool`, not `item.category` — category is the item's kind ("Clothing",
       "Paper"), so keying off it would have played the trash sound on treasure. */
    sound.playSfx(loot.pool === 'treasure' ? 'treasure_jingle' : 'coin');
  }, [loot]);

  /* Banked, and a level gained is the one moment worth its own fanfare. */
  useEffect(() => {
    if (claim && claim.profile.levels_gained.length > 0) sound.playSfx('level_up');
  }, [claim]);

  /* Count up rather than snap — the payout should feel earned. */
  useEffect(() => {
    if (!loot) return;
    const target = loot.coins;
    const t0 = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const k = Math.min(1, (now - t0) / 520);
      setShownCoins(Math.round(target * (1 - (1 - k) ** 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [loot]);

  if (!loot) return null;

  const isTreasure = loot.pool === 'treasure';
  const kyat = Number(loot.kyat_pending);
  const claimed = adState === 'verified';

  /* The server decides whether this rock has an ad at all, and most trash rocks now do not:
     `trash_double` is offered every third to fifth rock so that it stays worth noticing. A
     treasure rock always carries one — money rule 2. `null` means nothing to show, which is
     not the same as an offer the player is not eligible for, and `noAdReason` would be the
     wrong thing to say about it. */
  const offer = loot.ad ?? null;
  const canOffer = adsCanReward && offer !== null;

  async function watchAd() {
    if (!loot || !offer) return;
    setAdState('playing');
    setAdError(null);

    /*
     * Inside the ShweTap app, the shell owns this: it holds the AdMob SDK, and AdMob
     * signs its SSV callback only for a native impression. It runs the same three-leg
     * handshake this branch does — /ads/request, show, poll /ads/complete — against the
     * same user, and hands back what the SERVER said. See game/ads/nativeBridge.ts.
     */
    if (nativeBridge()) {
      const outcome = await requestNativeRewardedAd(offer.placement);
      if (outcome.status === 'rewarded' || outcome.status === 'unverified') {
        /* `unverified` is "the callback is late", not "no". Keep the impression: the
           server re-checks it at claim time, so a slow network does not cost the Kyat. */
        setImpression(outcome.impressionUuid);
        setAdState('verified');
      } else if (outcome.status === 'dismissed') {
        setAdState('skipped');
      } else {
        /* Could not play. Leave the offer up so it can be tried again — the impression the
           shell opened is abandoned server-side, and a fresh tap opens a new one. */
        setAdState('failed');
        setAdError(outcome.reason);
      }
      return;
    }

    try {
      const req = await ads.request(offer.placement);
      setImpression(req.impression_uuid);
      /* The real AdMob view happens here. Locally the dev server verifies at once
         (ADMOB_WEB_TRUSTED); in production this waits for the SSV callback. */
      await ads.complete(req.impression_uuid);
      setAdState('verified');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'AD_NOT_VERIFIED') {
        /* SSV has not landed yet. Keep the impression — the server re-checks it
           at claim time, so the player is not punished for a slow callback. */
        setAdState('verified');
        return;
      }
      setAdState('failed');
      setAdError(e instanceof ApiError ? e.message : 'The ad could not be played.');
    }
  }

  async function onCollect() {
    setBusy(true);
    try {
      await collect(claimed && impression ? impression : undefined);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      /* Centred, not bottom-anchored. Anchored to the bottom this left the top
         third of the phone as plain black at the exact moment the game is paying
         the player — the card reads as a sheet that failed to finish sliding up
         rather than as a reward. */
      className="absolute inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label="Loot found"
    >
      <div className="m-2.5 max-h-[94%] w-full max-w-[440px] overflow-y-auto rounded-lg
                      border border-ui-lit bg-[linear-gradient(var(--ui-panel-2),var(--cave-bg))]
                      p-5 pb-[calc(env(safe-area-inset-bottom)+18px)]">
        <div className="text-center">
          {/* The reveal moment — the one place the item is the whole screen,
              so it earns the largest render in the atlas. */}
          <div className="my-2 flex justify-center">
            {/* Lit and shadowed, like every other render in the game. These come
                off the same neutral studio backdrop as the rocks, and at 96px
                unlit the thing the player just earned was a small pale smudge in
                the middle of a warm card. */}
            <span
              className="inline-block"
              style={{
                filter:
                  'brightness(1.02) contrast(1.12) saturate(1.04) sepia(.12) hue-rotate(-6deg)' +
                  ' drop-shadow(0 10px 14px rgba(0,0,0,.6))',
              }}
            >
              <ItemIcon itemKey={loot.item.key} size={132} />
            </span>
          </div>
          <h2 className="text-xl font-extrabold">{loot.item.name}</h2>
          <p className="text-[11px] text-ink-dim">{loot.item.category}</p>
          <span
            className="mt-2 inline-block rounded-md border px-2 py-1 font-pixel text-[7px]"
            style={{ color: loot.item.rarity_color, borderColor: loot.item.rarity_color }}
          >
            {loot.item.rarity.toUpperCase()}
          </span>
          {loot.quantity > 1 && (
            <p className="mt-1 font-pixel text-[8px] text-[var(--ok)]">×{loot.quantity} DOUBLE LOOT</p>
          )}
        </div>

        <div className="my-3.5 flex flex-col gap-1.5">
          <Row icon="coin" label="Shwe Coins" value={`+${shownCoins.toLocaleString('en-US')}`} tone="coin" />

          {isTreasure && kyat > 0 && (
            <Row
              icon="kyat"
              label={
                claimed ? 'Shwe Kyat · claimed'
                  : adState === 'skipped' ? 'Shwe Kyat · forfeited'
                  : 'Shwe Kyat · locked'
              }
              value={`+${loot.kyat_pending}`}
              tone="kyat"
              locked={!claimed}
              struck={adState === 'skipped'}
            />
          )}

          <Row icon="xp" label="Miner XP" value={`+${loot.xp}`} tone="xp" />
        </div>

        {/*
          * Two states, because banking the loot is a moment worth landing.
          *
          * Before: the ad offer and "Collect and keep digging".
          * After: what was ACTUALLY banked, and one button out.
          *
          * It used to show neither. Collecting left the pre-claim rows on screen, the
          * orange button became a no-op (collect() returns early once the rock is
          * gone), and the only way out was a small underlined "Close". So the reward
          * arrived invisibly and the card looked broken — the player is told what they
          * *found*, never what they *kept*.
          */}
        {claim ? (
          <>
            <div className="my-3 rounded-md border border-[rgba(120,220,150,.30)]
                            bg-[rgba(40,90,55,.18)] p-3">
              <p className="text-center font-pixel text-[8px] tracking-wider text-[var(--ok)]">
                BANKED
              </p>
              <div className="mt-2.5 flex flex-col gap-1.5">
                <Row icon="coin" label="Shwe Coins"
                     value={`+${claim.granted.coins.toLocaleString('en-US')}`} tone="coin" />
                {Number(claim.granted.kyat) > 0 && (
                  <Row icon="kyat" label="Shwe Kyat" value={`+${claim.granted.kyat}`} tone="kyat" />
                )}
                {Number(claim.forfeited_kyat) > 0 && (
                  /* Shown, not hidden: skipping the ad is a choice, and a choice the
                     player cannot see the cost of is not a choice. */
                  <Row icon="void" label="Kyat forfeited"
                       value={claim.forfeited_kyat} tone="dim" />
                )}
                <Row icon="xp" label="Miner XP" value={`+${claim.granted.xp}`} tone="xp" />
                {claim.granted.extra_item && (
                  <Row icon="gift" label="Bonus find" value={claim.granted.extra_item} tone="xp" />
                )}
                {Number(claim.multiplier_applied) !== 1 && (
                  <Row icon="sparkle" label="Multiplier"
                       value={`×${claim.multiplier_applied}`} tone="kyat" />
                )}
              </div>
              <div className="mt-2.5 border-t border-[var(--hairline)] pt-2 text-center">
                <p className="text-[11px] text-ink-dim">
                  Wallet now{' '}
                  <b className="text-coin">{claim.wallet.coins.toLocaleString('en-US')}</b> coins
                  {Number(claim.wallet.kyat) > 0 && (
                    <> · <b className="text-kyat-glow">{claim.wallet.kyat}</b> Kyat</>
                  )}
                </p>
                {claim.profile.levels_gained.length > 0 && (
                  <p className="mt-1 font-pixel text-[8px] text-[var(--ok)]">
                    LEVEL UP → {claim.profile.level}
                  </p>
                )}
              </div>
            </div>

            <button
              type="button"
              onClick={dismiss}
              className="w-full rounded-sm border border-[rgba(255,220,170,.5)]
                         bg-[linear-gradient(#FFA945,#E0761C)] py-3 font-extrabold
                         text-[#2A1503] shadow-[0_4px_0_-1px_#8C440C]
                         active:translate-y-0.5"
            >
              Keep digging
            </button>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            {canOffer && adState !== 'verified' && adState !== 'skipped' && (
              <button
                type="button"
                onClick={() => void watchAd()}
                disabled={adState === 'playing'}
                /* The button wears the colour of what the ad actually pays.
                   It was always Kyat violet, including on a trash rock, where the
                   offer doubles COINS and can never mint Kyat — money rule 1. On
                   a screen about withdrawable money, dressing a coins-only offer
                   in the premium currency's colour is a miscue worth avoiding
                   even though the label underneath is correct. */
                className={`w-full rounded-sm border py-3 font-bold disabled:opacity-50 ${
                  isTreasure
                    ? 'border-[rgba(169,107,240,.35)] bg-[linear-gradient(#6B45A8,#3A2560)] text-kyat-glow'
                    : 'border-[rgba(245,197,66,.3)] bg-[linear-gradient(#4A3416,#2A1D0D)] text-coin'
                }`}
              >
                {adState === 'playing'
                  ? 'Playing ad…'
                  : (
                    <span className="inline-flex items-center gap-1.5">
                      <Icon name="ad" size={14} />
                      {isTreasure
                        ? `Watch ad → claim ${loot.kyat_pending} Shwe Kyat`
                        : 'Watch ad → 2× Shwe Coins'}
                    </span>
                  )}
              </button>
            )}

            {adState === 'failed' && adError && (
              <p className="text-center text-[10px] leading-relaxed text-[var(--danger,#FF9A8B)]">
                {adError} Your coins are safe either way.
              </p>
            )}

            <button
              type="button"
              onClick={() => void onCollect()}
              disabled={busy}
              className="w-full rounded-sm border border-[rgba(255,220,170,.5)]
                         bg-[linear-gradient(#FFA945,#E0761C)] py-3 font-extrabold text-[#2A1503]
                         shadow-[0_4px_0_-1px_#8C440C] active:translate-y-0.5 disabled:opacity-50"
            >
              {busy ? 'Collecting…' : 'Collect and keep digging'}
            </button>

            <p className="mt-1 text-center text-[10px] leading-relaxed text-ink-dim">
              {offer === null && adsCanReward
                /* Nothing on offer for this rock. Say so plainly rather than reaching for
                   `noAdReason`, which explains why ads are unavailable to this PLAYER — a
                   different and, here, untrue statement. */
                ? (isTreasure
                    ? 'Shwe Kyat only comes from a completed rewarded ad.'
                    : 'Trash rocks never drop Shwe Kyat. A coin-doubling ad turns up every few rocks.')
                : !adsCanReward
                /* No offer, so explain rather than leave a locked Kyat row unexplained.
                   The coins are banked either way — money rule 2. */
                ? noAdReason(flags)
                : isTreasure
                  ? 'Shwe Kyat only comes from a completed rewarded ad. Skip it and you '
                    + 'still keep the treasure and the coins.'
                  : 'Trash rocks never drop Shwe Kyat — the ad only doubles your coins.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, label, value, tone, locked, struck }: {
  icon: IconName;
  label: string;
  value: string;
  tone: 'coin' | 'kyat' | 'xp' | 'dim';
  locked?: boolean;
  struck?: boolean;
}) {
  const colour =
    tone === 'coin' ? 'text-coin'
      : tone === 'kyat' ? 'text-kyat-glow'
      /* A forfeit is information, not a reward — it must not read as a gain. */
      : tone === 'dim' ? 'text-ink-dim'
        /* XP was `text-rarity-rare`, a cold blue, on a card where every other
           number is warm. It also borrowed a *rarity* colour for something that
           has no rarity. Progress is the lamp's colour. */
        : 'text-[var(--torch-core)]';
  return (
    <div
      className={`flex items-center gap-2.5 rounded-xl border p-3 text-sm font-bold ${
        locked
          ? 'border-dashed border-[rgba(169,107,240,.4)] bg-[rgba(58,37,96,.35)]'
          : 'border-[var(--hairline)] bg-black/35'
      }`}
    >
      <span className={colour} aria-hidden><Icon name={icon} size={18} /></span>
      <span className="flex-1 text-xs font-semibold text-ink">{label}</span>
      <b className={`tabular ${colour} ${locked ? 'opacity-55' : ''} ${struck ? 'line-through' : ''}`}>
        {value}
      </b>
    </div>
  );
}
